import React, {useState} from "react";
import {Table, Button, Tag, Space} from "antd";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {Link} from "react-router-dom";
import {FormHeader} from "../shiftRequest/ShiftRequest.styles";
import {useTranslation} from "react-i18next";
import {EditOutlined, FileSearchOutlined} from "@ant-design/icons";
import {SubCompanyPaymentContractDetail} from "components/_defaultComponents/SubCompanyPaymentContractsTable/SubCompanyPaymentContractDetail/SubCompanyPaymentContractDetail";
import {useActiveContractDetail} from "components/_defaultComponents/SubCompanyPaymentContractsTable/useActiveContractDetail";
import {toTurkishProperCase} from "utils/toTurkishProperCase";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {useObserver} from "mobx-react-lite";
import {
    SubCompanyPricingFilters,
    DEFAULT_FILTERS
} from "./SubCompanyPricingFilters/SubCompanyPricingFilters";

export const SubCompanyPricingListPage = () => {
    const stores = useStore();
    const {t} = useTranslation(["subCompanyPricing"]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    // Filtreler - tek obje içinde
    const [filters, setFilters] = useState({
        ...DEFAULT_FILTERS,
        subCompanyWorkType: stores.meStore.map.filters.workType
    });

    const [isFiltersVisible, setIsFiltersVisible] = useState(false);

    // Sözleşme detayı satır bazında değil, tek modal örneği üzerinden açılıyor
    const {
        loadingSubCompanyId,
        contract: detailContract,
        open: handleOpenContractDetail,
        close: closeContractDetail
    } = useActiveContractDetail();

    // Seçili ücret tipini belirle
    const getActiveFeeType = () => {
        return filters.selectedFeeType ? [filters.selectedFeeType] : undefined;
    };

    const [subcompanyListLoading, subcompanyList, _, __, refetch] = useFetch({
        defaultDataValue: {dataCount: 0, rows: []},
        fetchFn: stores.companyStore.getSubCompanyPricingList.bind(null, {
            subCompanyWorkType: filters.subCompanyWorkType,
            city: filters.city,
            poolId: filters.poolId,
            companyId: filters.companyId,
            subCompanyId: filters.subCompanyId,
            subCompanyActivity:
                filters.subCompanyActivity === "all" ? undefined : filters.subCompanyActivity,
            hasActiveContract:
                filters.hasActiveContract === "all" ? undefined : filters.hasActiveContract,
            contractDocumentStatus:
                filters.contractDocumentStatus === "all"
                    ? undefined
                    : filters.contractDocumentStatus,
            feeType: getActiveFeeType(),
            courierAmountMin: filters.courierAmountMin
                ? parseInt(filters.courierAmountMin)
                : undefined,
            courierAmountMax: filters.courierAmountMax
                ? parseInt(filters.courierAmountMax)
                : undefined,
            clientAmountMin: filters.clientAmountMin
                ? parseInt(filters.clientAmountMin)
                : undefined,
            clientAmountMax: filters.clientAmountMax
                ? parseInt(filters.clientAmountMax)
                : undefined,
            courierMinimumDistanceMin: filters.courierMinimumDistanceMin
                ? parseInt(filters.courierMinimumDistanceMin)
                : undefined,
            courierMinimumDistanceMax: filters.courierMinimumDistanceMax
                ? parseInt(filters.courierMinimumDistanceMax)
                : undefined,
            clientMinimumDistanceMin: filters.clientMinimumDistanceMin
                ? parseInt(filters.clientMinimumDistanceMin)
                : undefined,
            clientMinimumDistanceMax: filters.clientMinimumDistanceMax
                ? parseInt(filters.clientMinimumDistanceMax)
                : undefined,
            courierGuaranteePackageAmountMin: filters.courierGuaranteePackageAmountMin
                ? parseFloat(filters.courierGuaranteePackageAmountMin)
                : undefined,
            courierGuaranteePackageAmountMax: filters.courierGuaranteePackageAmountMax
                ? parseFloat(filters.courierGuaranteePackageAmountMax)
                : undefined,
            clientGuaranteePackageAmountMin: filters.clientGuaranteePackageAmountMin
                ? parseFloat(filters.clientGuaranteePackageAmountMin)
                : undefined,
            clientGuaranteePackageAmountMax: filters.clientGuaranteePackageAmountMax
                ? parseFloat(filters.clientGuaranteePackageAmountMax)
                : undefined,
            limit: limit,
            skip: (currentPage - 1) * limit
        }),
        showLoading: true,
        dependencies: [JSON.stringify(filters), currentPage, limit]
    });

    // Helper fonksiyon - pricing'den belirli fee type'ı bul
    const getPricing = (pricings, feeType) => {
        return pricings?.find(p => p.feeType === feeType);
    };

    // Helper fonksiyon - fiyat formatla
    const formatPrice = amount => {
        return amount != null ? `${amount.toFixed(2).replace(".00", "")} ₺` : "-";
    };

    const columns = [
        {
            title: t("city", {ns: "subCompanyPricing"}),
            dataIndex: "city",
            key: "city",
            width: 100,
            render: text => (
                <Shortener
                    text={toTurkishProperCase(text)}
                    length={14}
                    render={text => text || "-"}
                />
            ),
            hidden: filters.subCompanyWorkType === "pool"
        },
        {
            title: t("pool", {ns: "subCompanyPricing"}),
            dataIndex: "poolName",
            key: "poolName",
            width: 100,
            render: (text, record) => (
                <Shortener
                    text={text}
                    length={20}
                    render={text => (
                        <Link to={`/pools/${record.poolId}`} target="_blank">
                            {text}
                        </Link>
                    )}
                />
            ),
            // Acentede Dedike listesinde havuza bağlı (dedike entegrasyon) alt firmalar da
            // yer aldığı için havuz kolonu her iki kovada da gösteriliyor
            hidden:
                !stores.meStore.checkRole("isAgency") && filters.subCompanyWorkType === "dedicated"
        },
        {
            title: t("company", {ns: "subCompanyPricing"}),
            dataIndex: "companyName",
            key: "companyName",
            width: 100,
            render: (text, record) => (
                <Shortener
                    text={text}
                    length={10}
                    render={text => (
                        <Link to={`/companies/${record.companyId}`} target="_blank">
                            {text}
                        </Link>
                    )}
                />
            )
        },
        {
            title: t("subCompany", {ns: "subCompanyPricing"}),
            dataIndex: "name",
            key: "name",
            width: 120,
            render: (text, record) => (
                <Shortener
                    text={text}
                    length={20}
                    render={text => (
                        <Link to={`/companies/sub-company/${record._id}`} target="_blank">
                            {text}
                        </Link>
                    )}
                />
            )
        },
        {
            title: t("hasActiveContract", {ns: "subCompanyPricing"}),
            dataIndex: "hasActiveContract",
            key: "hasActiveContract",
            width: 80,
            align: "center",
            render: hasContract => (
                <Tag color={hasContract ? "green" : "red"}>
                    {hasContract
                        ? t("yes", {ns: "subCompanyPricing"})
                        : t("no", {ns: "subCompanyPricing"})}
                </Tag>
            )
        },
        {
            title: t("paketPrice", {ns: "subCompanyPricing"}),
            key: "paket",
            width: 80,
            render: (_, record) => {
                const pricing = getPricing(record.pricings, "PAKET");
                if (!pricing) return "-";
                return (
                    <Tag>
                        <div>
                            <strong>{t("clientPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.clientAmount)}
                        </div>
                        <div>
                            <strong>{t("courierPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.courierAmount)}
                        </div>
                    </Tag>
                );
            }
        },
        {
            title: t("distancePrice", {ns: "subCompanyPricing"}),
            key: "mesafe",
            width: 80,
            render: (_, record) => {
                const pricing = getPricing(record.pricings, "KM");
                if (!pricing) return "-";
                return (
                    <Tag>
                        <div>
                            <strong>{t("clientPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.clientAmount)}
                        </div>
                        <div>
                            <strong>{t("courierPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.courierAmount)}
                        </div>
                        <div>
                            <strong>{t("courierMinDistance", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {pricing.courierMinimumDistance || 0} km
                        </div>
                        <div>
                            <strong>{t("clientMinDistance", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {pricing.clientMinimumDistance || 0} km
                        </div>
                    </Tag>
                );
            }
        },
        {
            title: t("hourlyPrice", {ns: "subCompanyPricing"}),
            key: "saat",
            width: 80,
            render: (_, record) => {
                const pricing = getPricing(record.pricings, "SAATLIK_UCRET");
                if (!pricing) return "-";
                return (
                    <Tag>
                        <div>
                            <strong>{t("clientPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.clientAmount)}
                        </div>
                        <div>
                            <strong>{t("courierPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.courierAmount)}
                        </div>
                    </Tag>
                );
            }
        },
        {
            title: t("guaranteedHourlyPrice", {ns: "subCompanyPricing"}),
            key: "garantiSaat",
            width: 80,
            render: (_, record) => {
                const pricing = getPricing(record.pricings, "SAATLIK_GARANTI_PAKET");
                if (!pricing) return "-";
                return (
                    <Tag>
                        <div>
                            <strong>{t("clientPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.clientGuaranteePackageAmount)}
                        </div>
                        <div>
                            <strong>{t("courierPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.courierGuaranteePackageAmount)}
                        </div>
                    </Tag>
                );
            }
        },
        {
            title: t("bonusPackagePrice", {ns: "subCompanyPricing"}),
            key: "bonusPackagePrice",
            width: 80,
            render: (_, record) => {
                const pricing = getPricing(record.pricings, "BONUS_PAKET_KAZANCI_UCRETI");
                if (!pricing) return "-";
                return (
                    <Tag>
                        <div>
                            <strong>{t("clientPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.clientAmount)}
                        </div>
                        <div>
                            <strong>{t("courierPrice", {ns: "subCompanyPricing"})} :</strong>{" "}
                            {formatPrice(pricing.courierAmount)}
                        </div>
                    </Tag>
                );
            }
        },

        {
            title: t("createdBy", {ns: "subCompanyPricing"}),
            dataIndex: "createdBy",
            key: "createdBy",
            width: 120,
            render: text => text || "-"
        },
        {
            title: t("action", {ns: "subCompanyPricing"}),
            key: "action",
            width: 100,
            align: "center",
            render: (_, record) => (
                <Space size={4}>
                    {!stores.meStore.checkRole("isAgency") && (
                        <Link to={`/sub-company/payment-contract/${record._id}`} target="_blank">
                            <Button type="primary" size="small" icon={<EditOutlined />} />
                        </Link>
                    )}
                    <Button
                        size="small"
                        icon={<FileSearchOutlined />}
                        disabled={!record.hasActiveContract}
                        loading={loadingSubCompanyId === record._id}
                        onClick={() => handleOpenContractDetail(record._id)}
                    />
                </Space>
            )
        }
    ];

    const handleFilterChange = newFilters => {
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            ...DEFAULT_FILTERS,
            subCompanyWorkType: stores.meStore.map.filters.workType
        });
        setCurrentPage(1);
    };

    return useObserver(() => (
        <div style={{display: "flex", flexDirection: "column", overflow: "scroll"}}>
            <FormHeader>{t("title", {ns: "subCompanyPricing"})}</FormHeader>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <SubCompanyPricingFilters
                    filters={filters}
                    onChange={handleFilterChange}
                    onReset={handleResetFilters}
                    isVisible={isFiltersVisible}
                    onVisibilityChange={() => setIsFiltersVisible(!isFiltersVisible)}
                />
                <div style={{overflowX: "auto"}}>
                    <Table
                        columns={columns.filter(column => !column.hidden)}
                        size="small"
                        dataSource={subcompanyList?.rows}
                        loading={subcompanyListLoading}
                        rowKey="_id"
                        //scroll={{x: 1800}}
                        pagination={{
                            showSizeChanger: false,
                            current: currentPage,
                            pageSize: 10,
                            total: subcompanyList?.dataCount,
                            onChange: page => setCurrentPage(page),
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} ${t("records", {
                                    ns: "subCompanyPricing"
                                })}`
                        }}
                    />
                </div>
            </div>
            {detailContract && (
                <SubCompanyPaymentContractDetail
                    subCompanyPaymentContract={detailContract}
                    visible={true}
                    onClose={closeContractDetail}
                />
            )}
        </div>
    ));
};
